<script setup lang="ts">
import { $isListNode } from "@lexical/list";
import { mergeRegister } from "@lexical/utils";
import { useEventListener } from "@vueuse/core";
import {
  $addUpdateTag,
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $onUpdate,
  COMMAND_PRIORITY_EDITOR,
  SKIP_DOM_SELECTION_TAG,
  type ElementNode,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onBeforeUnmount, onMounted, onUnmounted, ref } from "vue";

import actions from "~/actions";
import { backendOld } from "~/api";
import { LANGUAGE_SET } from "~/constants/language";
import { FeedbackRating, Language, RecommendationAction, RecommendationKind } from "~/shared/enums";

import {
  EVENT_BLUR_OVERALL,
  EVENT_CLEANUP_RECOMMENDATION_NODE,
  EVENT_GENERATE_RECOMMENDATION,
  EVENT_OPEN_RECOMMENDATION_OVERLAY,
  EVENT_OPEN_RECOMMENDATIONS,
  RECOMMENDATION_KINDS_ALLOWED_ON_EMPTY_EDITOR,
  RECOMMENDATION_KINDS_OVERLAY,
} from "../const";
import { $createAiCursorNode } from "../nodes/AiCursorNode";
import {
  $createRecommendationButtonsNode,
  $isRecommendationButtonsNode,
  RecommendationButtonsNode,
} from "../nodes/RecommendationButtonsNode";
import { $createRecommendationLoadingNode, RecommendationLoadingNode } from "../nodes/RecommendationLoadingNode";
import {
  $createRecommendationWrapperNode,
  $isRecommendationWrapperNode,
  RecommendationWrapperNode,
} from "../nodes/RecommendationWrapperNode";
import { fillNodeWithMarkdown } from "../transformers";
import { $getSelectionAsMarkdown, isEmpty, streamRecommendation, useStreamText } from "../utils";
import { markSelection, removeMarkById, replaceMarkedSelection } from "../utils/markUtils";
import RecommendationOverlay from "./RecommendationOverlay.vue";

const props = defineProps<{
  duid: string;
  baseText?: boolean;
  toolbarTeleportKey: string;
}>();

const editor = useLexicalComposer();

const activeWrapperKey = ref<string | null>(null);
const activeChildKey = ref<string | null>(null);

const getContentChildren = (wrapperNode: RecommendationWrapperNode) =>
  wrapperNode.getChildren().filter((e) => !$isRecommendationButtonsNode(e));

const insertContentChildren = (wrapperNode: RecommendationWrapperNode) => {
  getContentChildren(wrapperNode).forEach((e) => wrapperNode.insertBefore(e));
};

const fillInRecommendation = async (
  wrapperNode: ElementNode,
  kind: RecommendationKind,
  language?: Language,
  selectedText?: string | null
) => {
  let recommendationDuid = "";
  const updateNode = (text: string, end: boolean) => {
    editor.update(() => {
      // User deleted the node before the request finished
      if ($getNodeByKey(wrapperNode.getKey()) === null) {
        return;
      }

      $addUpdateTag(SKIP_DOM_SELECTION_TAG);
      fillNodeWithMarkdown(wrapperNode, text);

      if (!end) {
        // AI cursor
        const lastChild = wrapperNode.getLastChild();
        const targetNode = $isListNode(lastChild) ? lastChild.getLastChild() : lastChild;
        if (!targetNode || !$isElementNode(targetNode)) {
          return;
        }
        targetNode.append($createAiCursorNode());
      } else {
        // Add buttons
        wrapperNode.append($createRecommendationButtonsNode(kind, recommendationDuid));
      }
    });
  };

  let previousText = "";
  const { streamText, stop } = useStreamText((text) => {
    updateNode(text, false);
  });

  await streamRecommendation({
    duid: props.duid,
    kind,
    selectedText: selectedText ?? null,
    language,
    onUpdate: (text, duid, end) => {
      if (text === "" && end) {
        // Remove wrapper if text is empty at the end
        stop();
        editor.update(() => {
          $getNodeByKey(wrapperNode.getKey())?.remove();
        });
        return;
      }

      if (end) {
        recommendationDuid = duid;
        stop();
        updateNode(text, true);
      } else {
        streamText(text.substring(previousText.length));
        previousText = text;
      }
    },
    onError: () => {
      stop();
      editor.update(() => {
        $getNodeByKey(wrapperNode.getKey())?.remove();
      });
    },
  });
};

const recommend = (kind: RecommendationKind | Language, parentKey: string | null): boolean => {
  if (kind === RecommendationKind.PROPERTIES) {
    actions.visualization.recommendProperties();
    return true;
  }
  if (kind === RecommendationKind.SUBTASKS) {
    actions.visualization.recommendSubtasks();
    return true;
  }

  editor.getRootElement()?.focus({ preventScroll: true });

  let kindNorm: RecommendationKind;
  let language: Language | undefined;
  const kindAsLanguage = kind as Language;
  if (LANGUAGE_SET.has(kindAsLanguage)) {
    kindNorm = RecommendationKind.TRANSLATE;
    language = kindAsLanguage;
  } else {
    kindNorm = kind as RecommendationKind;
  }

  editor.update(() => {
    const rootNode = $getRoot();
    if (rootNode.getTextContent().trim() === "" && !RECOMMENDATION_KINDS_ALLOWED_ON_EMPTY_EDITOR.has(kindNorm)) {
      return;
    }

    const selection = $getSelection();
    const hasSelection = $isRangeSelection(selection) && !selection.isCollapsed();
    const selectionHasContent = hasSelection && selection.getTextContent().trim() !== "";

    const selectedMarkdown = selectionHasContent ? $getSelectionAsMarkdown(editor) : null;
    const markResult = selectionHasContent ? markSelection() : null;
    const markId = markResult?.markId ?? null;

    const shouldUseOverlay =
      RECOMMENDATION_KINDS_OVERLAY.has(kindNorm) &&
      (kindNorm === RecommendationKind.CUSTOM || (markId && $isRangeSelection(selection)));

    if (shouldUseOverlay) {
      editor.dispatchCommand(EVENT_OPEN_RECOMMENDATION_OVERLAY, {
        markId,
        kind: kindNorm,
        language,
        selectedMarkdown,
      });
    } else {
      const wrapperNode = $createRecommendationWrapperNode(kindNorm);
      activeWrapperKey.value = wrapperNode.getKey();
      if (markId) {
        wrapperNode.setMarkId(markId);
      }
      fillInRecommendation(wrapperNode, kindNorm, language, selectedMarkdown);
      const loadingNode = $createRecommendationLoadingNode(kindNorm);
      wrapperNode.append(loadingNode);

      const parentNode = parentKey ? $getNodeByKey(parentKey) : null;
      if (parentNode) {
        parentNode.insertAfter(wrapperNode);
        if (!parentNode.getTextContent().trim()) {
          parentNode.remove();
        }
      } else {
        rootNode.append(wrapperNode);
      }
    }
  });

  return true;
};

const cleanupRecommendationNode = (
  childNodeKey: string,
  action: RecommendationAction,
  recommendationDuid: string | undefined
): boolean => {
  const childNode = $getNodeByKey(childNodeKey);
  if (childNode === null) {
    return false;
  }
  const wrapperNode = childNode.getParent();
  if (wrapperNode === null || !$isRecommendationWrapperNode(wrapperNode)) {
    return false;
  }

  const markId = wrapperNode.getMarkId();

  if (action === RecommendationAction.REPLACE) {
    if (markId) {
      replaceMarkedSelection(markId);
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return false;
      }
      const contentNodes = getContentChildren(wrapperNode).filter(
        (node) => !$isElementNode(node) || node.getChildren().length > 0
      );
      if (contentNodes.length) {
        selection.insertNodes(contentNodes);
      }
    } else {
      wrapperNode.getPreviousSiblings().forEach((e) => e.remove());
      insertContentChildren(wrapperNode);
    }
  } else if (action === RecommendationAction.KEEP) {
    insertContentChildren(wrapperNode);
    if (markId) {
      removeMarkById(markId);
    }
  } else if (action === RecommendationAction.DISCARD && markId) {
    removeMarkById(markId);
  }

  wrapperNode.remove();
  $onUpdate(() => {
    editor.getRootElement()?.focus({ preventScroll: true });
    editor.focus();
  });

  if (recommendationDuid === undefined) {
    return true;
  }

  backendOld.recommendations.provideFeedback(
    recommendationDuid,
    action === RecommendationAction.DISCARD ? FeedbackRating.BAD : FeedbackRating.GOOD
  );
  return true;
};

const keepAllRecommendations = (): boolean =>
  // TODO enable this when focus is tracked completely (i.e. dropdowns and buttons count as focus)
  // const rootNode = $getRoot();
  // rootNode
  //   .getChildren()
  //   .filter((e) => $isRecommendationWrapperNode(e))
  //   .forEach((wrapper) => {
  //     const buttonsNode = wrapper.getChildren().find((e: LexicalNode) => $isRecommendationButtonsNode(e));
  //     if (buttonsNode === undefined) return;
  //     cleanupRecommendationNode(buttonsNode.getKey(), RecommendationAction.KEEP);
  //   });
  true;
const openRecommendations = () => {
  editor.getRootElement()?.focus({ preventScroll: true });
  editor.update(() => {
    const rootNode = $getRoot();
    const lastChild = rootNode.getLastChild();
    if (lastChild && isEmpty(lastChild)) {
      lastChild.remove();
    }
    const paragraphNode = $createParagraphNode();
    paragraphNode.append($createTextNode("/"));
    rootNode.append(paragraphNode);
    paragraphNode.select();
  });
  return true;
};

const clearActiveRecommendation = () => {
  editor.update(
    () => {
      const wrapper = activeWrapperKey.value ? $getNodeByKey<RecommendationWrapperNode>(activeWrapperKey.value) : null;
      if (wrapper) {
        $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        const markId = wrapper.getMarkId();
        if (markId) {
          removeMarkById(markId);
        }
        wrapper.remove();
      }
    },
    { discrete: true }
  );
};

useEventListener(window, "pagehide", clearActiveRecommendation);
onBeforeUnmount(clearActiveRecommendation);

let unregisterListeners: () => void;

onMounted(() => {
  if (!editor.hasNodes([RecommendationButtonsNode, RecommendationLoadingNode, RecommendationWrapperNode])) {
    throw new Error(
      "RecommendationPlugin: some of [RecommendationButtonsNode, RecommendationLoadingNode, RecommendationWrapperNode] not registered on editor"
    );
  }

  unregisterListeners = mergeRegister(
    editor.registerCommand(EVENT_BLUR_OVERALL, keepAllRecommendations, COMMAND_PRIORITY_EDITOR),
    editor.registerCommand(
      EVENT_GENERATE_RECOMMENDATION,
      (payload: { kind: RecommendationKind | Language; parentKey: string | null }) =>
        recommend(payload.kind, payload.parentKey),
      COMMAND_PRIORITY_EDITOR
    ),
    editor.registerCommand(
      EVENT_CLEANUP_RECOMMENDATION_NODE,
      (payload: { key: string; action: RecommendationAction; recommendationDuid?: string }) =>
        cleanupRecommendationNode(payload.key, payload.action, payload.recommendationDuid),
      COMMAND_PRIORITY_EDITOR
    ),
    editor.registerCommand(EVENT_OPEN_RECOMMENDATIONS, openRecommendations, COMMAND_PRIORITY_EDITOR),
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const wrapper = activeWrapperKey.value
          ? $getNodeByKey<RecommendationWrapperNode>(activeWrapperKey.value)
          : null;
        activeWrapperKey.value = wrapper?.isAttached() ? wrapper.getKey() : null;
        activeChildKey.value = wrapper?.getFirstChild()?.getKey() ?? null;
      });
    })
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});

defineExpose({
  openRecommendations,
});
</script>

<template>
  <RecommendationOverlay
    :duid="duid"
    :active-wrapper-key="activeWrapperKey"
    :active-child-key="activeChildKey"
    :base-text="baseText"
    :toolbar-teleport-key="toolbarTeleportKey" />
</template>
